// [APP-FORK] 외부(일반 카메라) 스캔 전용 랜딩 페이지 (admin = verify 담당).
// QR 웹 URL(https://{도메인}/app/link/verify/{uuid})을 브라우저가 열면 이 페이지가:
//   1) 앱 딥링크(naegift://verify/{uuid})로 앱 열기 시도
//   2) 앱이 안 열리면(타이머 발화) 스토어로 유도(현재 스토어 URL 미정 → "준비중" 안내)
// ★인증 API 호출 없음 — 로그인 불필요 공개 페이지. (앱 스캐너는 이 랜딩을 거치지 않고
//   Flutter가 /app/qr 역매핑으로 직행하므로, 이 페이지는 외부 카메라 전용이다.)
// verify type 담당(store/gift 는 user-app 소관).
import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

// OS별 스토어 URL — 앱은 하나이므로 naegift-user-app/src/app/storeUrls.ts:6-7 과 동일 값.
const ANDROID_STORE =
  'https://play.google.com/store/apps/details?id=com.naegift.app';
const IOS_STORE = 'https://apps.apple.com/kr/app/id6791131122';

// 이 랜딩이 담당하는 type (verify). store/gift 는 user-app 랜딩 소관.
const VALID_TYPES = ['verify'];

function detectOs() {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return 'other';
}

export function storeUrlForOs() {
  return detectOs() === 'ios' ? IOS_STORE : ANDROID_STORE;
}

// [APP-FORK] 아래 intent:// 의 package= 값 — 현재 접속 도메인 환경(dev/qa/prod)에 맞는 flavor 패키지.
//  ★왜 필요한가: intent URL 에 package= 가 있으면 안드로이드는 App Link 검증·host 와 무관하게
//    그 패키지를 직접 실행한다. prod 로 하드코딩돼 있던 동안은 shopadmin-dev / shopadmin-qa
//    도메인에서도 prod 앱이 열렸다(dev/qa 앱이 설치돼 있어도 무시됐다).
//  ★패키지·suffix 근거: naegift_mobile_app/android/app/build.gradle.kts
//    :46 applicationId "com.naegift.app" / :78 dev suffix ".dev" / :86 qa ".qa".
//    모바일 앱은 하나뿐이고 동일 MainActivity 가 shopadmin·shopuser 두 host 를 다 받으므로
//    (AndroidManifest.xml:54, :66) user-app 랜딩(src/app/appPackage.ts)과 같은 규칙·같은 패키지다.
//  ★판정을 서브도메인(첫 라벨) 끝으로 하는 이유: 실제 도메인이 shopadmin-dev / shopadmin-qa /
//    shopadmin 이고(.github/workflows/cd-*.yml), hostname 을 써야 포트에 흔들리지 않으며,
//    단순 includes('-dev') 와 달리 다른 라벨에 '-dev' 가 섞여도 오판하지 않는다.
//    매칭 없는 호스트(localhost 등)는 기본 prod — 하드코딩 시절과 동일 동작.
export function androidAppPackage() {
  const base = 'com.naegift.app';
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname || '' : '';
  const subdomain = hostname.split('.')[0];
  if (subdomain.endsWith('-dev')) return `${base}.dev`;
  if (subdomain.endsWith('-qa')) return `${base}.qa`;
  return base;
}

// [APP-FORK] iOS 커스텀 스킴(naegift://)도 환경별로 갈라야 한다 — androidAppPackage()와 같은 이유,
//  다른 메커니즘(iOS엔 package= 같은 명시 지정 필드가 없어 스킴 문자열 자체가 유일한 식별자다).
//  dev/qa/prod 앱 3개가 전부 'naegift'를 등록하면 iOS가 그중 하나만(임의로) 열어버린다.
//  ★스킴 값 근거: naegift_mobile_app/ios/Flutter/Flavors/{Dev,Qa,Prod}.xcconfig의
//    APP_URL_SCHEME, lib/config.dart AppConfig.urlScheme과 반드시 동일해야 한다(3곳 일치 필수).
//    user-app의 appPackage.ts iosAppScheme()과도 동일 규칙.
export function iosAppScheme() {
  const hostname =
    typeof window !== 'undefined' ? window.location.hostname || '' : '';
  const subdomain = hostname.split('.')[0];
  if (subdomain.endsWith('-dev')) return 'naegift-dev';
  if (subdomain.endsWith('-qa')) return 'naegift-qa';
  return 'naegift';
}

export default function AppLinkLanding() {
  const { type, uuid } = useParams();
  // opening: 앱 열기 시도 중 / store: 미설치 → 준비중 안내 / invalid: 잘못된 링크
  const [phase, setPhase] = useState('opening');
  const timerRef = useRef(null);

  useEffect(() => {
    // 유효성: type 이 verify 이고 uuid 존재
    if (!uuid || !VALID_TYPES.includes(type)) {
      setPhase('invalid');
      return;
    }

    const goStore = () => {
      // 앱 미설치(타이머 발화) → 스토어. URL 있으면 이동, 없으면 준비중 안내만.
      const url = storeUrlForOs();
      if (url) {
        window.location.href = url;
      } else {
        setPhase('store');
      }
    };

    // ?fallback=1 재진입 = 안드로이드에서 앱이 없어 intent fallback_url로 되돌아온 경우.
    //  딥링크 재시도 없이(무한루프 방지) 바로 스토어/준비중 처리.
    const isFallback =
      new URLSearchParams(window.location.search).get('fallback') === '1';
    if (isFallback) {
      goStore();
      return;
    }

    // 앱이 열려 탭이 백그라운드로 가면 타이머 취소(스토어 안 감).
    const onVisibility = () => {
      if (document.hidden && timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);

    // 딥링크로 앱 열기 시도. OS별 분기(안드=intent URL, iOS/기타=커스텀 스킴).
    const buildDeepLink = () => {
      if (detectOs() === 'android') {
        // 안드로이드 크롬/삼성인터넷은 순수 커스텀 스킴을 window.location으로 안 열어 intent URL 사용.
        // 앱 없으면 크롬이 S.browser_fallback_url(?fallback=1)로 재이동 → 위 isFallback 분기가 준비중 처리
        //  (fallback_url 없으면 "항목을 찾을 수 없습니다" 에러페이지가 뜸).
        const fbUrl = encodeURIComponent(
          window.location.origin + window.location.pathname + '?fallback=1'
        );
        return `intent://${type}/${uuid}#Intent;scheme=naegift;package=${androidAppPackage()};S.browser_fallback_url=${fbUrl};end`;
      }
      // iOS/기타: 커스텀 스킴(iOS 사파리는 스킴 동작). 환경별 분기 — 위 iosAppScheme() 주석 참조.
      return `${iosAppScheme()}://${type}/${uuid}`;
    };
    const deepLink = buildDeepLink();
    window.location.href = deepLink;

    // 2.5초 내 앱이 안 열리면 스토어.
    timerRef.current = setTimeout(goStore, 2500);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, uuid]);

  const onDownloadClick = () => {
    const url = storeUrlForOs();
    if (url) {
      window.location.href = url;
    } else {
      // 스토어 URL 미정 — 준비중.
      // eslint-disable-next-line no-alert
      window.alert('앱 준비중입니다. 잠시 후 다시 시도해주세요.');
    }
  };

  const wrapStyle = {
    minHeight: '60vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    padding: 24,
    textAlign: 'center',
  };

  if (phase === 'invalid') {
    return (
      <div style={wrapStyle}>
        <div style={{ fontSize: 40 }}>🔗</div>
        <p style={{ fontSize: 16, color: '#333' }}>잘못된 링크입니다.</p>
        <p style={{ fontSize: 13, color: '#888' }}>QR을 다시 확인해주세요.</p>
      </div>
    );
  }

  if (phase === 'store') {
    return (
      <div style={wrapStyle}>
        <div style={{ fontSize: 40 }}>🎁</div>
        <p style={{ fontSize: 16, color: '#333' }}>앱 준비중입니다</p>
        <p style={{ fontSize: 13, color: '#888' }}>
          앱이 설치되어 있지 않은 것 같아요. 곧 다운로드가 열립니다.
        </p>
        <button
          type="button"
          onClick={onDownloadClick}
          style={{
            padding: '10px 20px',
            borderRadius: 8,
            border: 'none',
            background: '#8AAAE5',
            color: '#fff',
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          다운로드
        </button>
      </div>
    );
  }

  // opening
  return (
    <div style={wrapStyle}>
      <div style={{ fontSize: 40 }}>📱</div>
      <p style={{ fontSize: 16, color: '#333' }}>앱으로 이동 중...</p>
      <p style={{ fontSize: 13, color: '#888' }}>
        앱이 없으면 잠시 후 안내 화면으로 전환됩니다.
      </p>
    </div>
  );
}
